import { action, makeObservable, observable, computed, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { MrbSelectStore } from "mrb/components/select";
import { DropdownItemsResult } from "mrb/components/select/models";
import { httpStatusCodes } from "mrb/core/constants";
import { localizationService } from "common/localization";
import CertifierContentListViewStore from "./CertifierContentListViewStore";

class CertifierDashboardViewStore extends MrbBaseViewStore {
    @observable.ref certifierModes = [];
    @observable selectedModeId = null;
    @observable selectedTab = "station";

    tabOptions = [
        { id: "station", name: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.TABS.STATIONS") },
        { id: "podcast", name: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.TABS.PODCASTS") },
        { id: "album", name: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.TABS.ALBUMS") },
    ];

    @computed get hasMultipleModes() {
        return this.certifierModes.length > 1;
    }

    @computed get selectedMode() {
        if (!this.selectedModeId || !this.certifierModes.length) return null;
        return this.certifierModes.find((m) => m.id === this.selectedModeId);
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;

        this.modeSelectStore = new MrbSelectStore(
            {
                isSearchable: false,
                isClearable: false,
                virtualized: false,
                initFetch: false,
                textKey: "name",
                valueKey: "id",
                placeholder: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.MODE_SELECTOR.PLACEHOLDER"),
                actions: {
                    onChange: async (option) => {
                        if (option) {
                            await this.selectMode(option.value);
                        }
                    },
                },
            }
        );

        this.contentListViewStore = new CertifierContentListViewStore(rootStore, {
            routeStore,
            dashboardViewStore: this,
        });
    }

    async onInit() {
        await this.loadCertifierModes();
    }

    @action.bound
    async loadCertifierModes() {
        try {
            const data = await this.routeStore.findCertifierModes({ page: 1, rpp: 100 });
            runInAction(() => {
                this.certifierModes = data.item || [];

                if (this.certifierModes.length > 0) {
                    this.modeSelectStore.originalItems = new DropdownItemsResult(
                        this.certifierModes,
                        1,
                        this.certifierModes.length,
                        this.certifierModes.length
                    );
                    this.selectedModeId = this.certifierModes[0].id;
                    this.modeSelectStore.setSelectedItem(this.certifierModes[0]);
                }
            });

            if (this.selectedModeId) {
                await this.contentListViewStore.queryUtility.fetch();
            }
        } catch (err) {
            if (err && err.statusCode === httpStatusCodes.CanceledRequest) {
                return;
            }
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.NOTIFICATIONS.LOAD_ERROR"),
                err
            );
        }
    }

    @action.bound
    async fetchSummary() {
        // This is a placeholder for summary fetching if needed in the future
        // For now we just return to avoid errors in the list store
        return Promise.resolve();
    }

    @action.bound
    async selectMode(modeId) {
        this.selectedModeId = modeId;
        this.contentListViewStore.clearSelection();
        await this.contentListViewStore.queryUtility.fetch();
    }

    @action.bound
    async setSelectedTab(tabId) {
        this.selectedTab = tabId;
        this.contentListViewStore.setContentTypeFilter(tabId);
    }
}

export default CertifierDashboardViewStore;
